// PTZ AW-RP50 — per-panel help data.
//
// Hotspots resolve against `[data-hotspot-id]` attributes on the
// panel's controls; the HotspotLayer measures live element rects
// at runtime so badges stay aligned through any re-layout.
//
// HARD RULES applied:
//   • #1 source authority — facts traced to Panasonic AW-RP50
//     operating instructions and CO-160 (no BTV / CVS / branch).
//   • #2 no fabricated citations — copy paraphrases manuals, no
//     scripture or paragraph numbers attributed.
//   • #8 no internal sources — welcome card is plain prose.

import type { PanelHelpData } from "../../help/help-types";

export const PTZ_AW_RP50_HELP: PanelHelpData = {
  welcome: {
    title: "Panasonic AW-RP50",
    eyebrow: "Video Operations · PTZ camera controller",
    body:
      "This is the controller you'll use to drive the convention's pan-tilt-zoom cameras. The CAMERA SELECT row at the top-left chooses which camera you're driving; everything else on the panel — joystick, zoom rocker, dials, presets — routes to that selected camera. The center joystick pans and tilts; the rocker beside it zooms in and out. The right side holds the preset pad: ten numbered shots (Wide, Lectern, Spkr MCU, Chairman, Baptism, etc.) you recall with one button press. In IC Manila mode, one operator drives all three PTZ cams from this panel; in standard-assembly mode, only the seat's assigned cam is enabled.",
    highlights: [
      "CAMERA SELECT — the row that decides which camera the rest of the panel controls.",
      "Joystick + zoom rocker — live pan, tilt, zoom on the selected cam.",
      "PRESET RECALL pad — ten stored shots; one press snaps the camera there.",
      "STORE — saves the current framing back into the active preset slot.",
      "SPEED — pan/tilt rate; lower numbers move slower for smoother live moves.",
      "The full hardware face is shown for orientation; only the labelled controls (CAMERA SELECT, joystick, zoom rocker, presets, STORE, RECALL, SPEED) respond. The dials, MENU/OSD/AWB/ABB row, and USER buttons are illustrative.",
    ],
  },
  hotspots: [
    {
      id: "cam-select",
      targetSelector: "[data-hotspot-id='cam-select']",
      label: "Camera Select",
      body:
        "The three buttons that pick which camera the rest of the panel drives. The lit button is the active camera. In standard-assembly mode only the seat's assigned cam is enabled; the others gray out.",
    },
    {
      id: "lcd",
      targetSelector: "[data-hotspot-id='lcd']",
      label: "Status LCD",
      body:
        "Read-only summary of the active camera: which preset was last recalled, current zoom percentage, iris, tally state (PGM red / PVW green), and current mode. Use this to confirm the controller is pointed at the camera you think it is.",
    },
    {
      id: "joystick",
      targetSelector: "[data-hotspot-id='joystick']",
      label: "Pan/tilt joystick",
      body:
        "Drag (or use arrow keys) to pan and tilt the active camera live. Movement rate is governed by the SPEED setting on the right. Smooth, deliberate moves only — jerky on-air corrections are visible to the audience.",
    },
    {
      id: "zoom-rocker",
      targetSelector: "[data-hotspot-id='zoom-rocker']",
      label: "Zoom rocker",
      body:
        "Wide / Tele zoom on the active camera. Press +/− to step. Like the joystick, zoom is live and visible on PROGRAM if this camera is on air — frame in PVW first when possible.",
    },
    {
      id: "dial-row",
      targetSelector: "[data-hotspot-id='dial-row']",
      label: "Iris · Focus · Pedestal",
      body:
        "Manual exposure and focus adjustments for the active camera. For convention work these are typically left at AUTO/AWB defaults; consult the senior brother on assignment before changing them on air.",
    },
    {
      id: "menu-wb",
      targetSelector: "[data-hotspot-id='menu-wb']",
      label: "Menu · White Balance",
      body:
        "MENU/OSD bring up on-camera menus on the multiview tile. AWB (auto white balance) and ABB (auto black balance) calibrate the camera for the venue's lighting and should only be run during pre-program checks, not while the camera is on air.",
    },
    {
      id: "preset-pad",
      targetSelector: "[data-hotspot-id='preset-pad']",
      label: "Preset Recall pad",
      body:
        "Ten numbered keys — one press snaps the active camera to the stored framing for that preset (Wide, Lectern, Speaker MCU, Chairman, Baptism, audience tiles, Stage Wide, Establish). The keys you'll use most are the named program shots; learn the layout so you can recall by feel.",
    },
    {
      id: "preset-controls",
      targetSelector: "[data-hotspot-id='preset-controls']",
      label: "Store · Recall",
      body:
        "STORE writes the camera's current pan/tilt/zoom into the active preset slot — don't press it on air. RECALL re-fires the last preset, useful if a moved camera needs to snap back to its known framing.",
    },
    {
      id: "user-buttons",
      targetSelector: "[data-hotspot-id='user-buttons']",
      label: "User buttons",
      body:
        "User-assignable shortcuts. The convention's controllers are configured during installation; verify the assignments on your seat's cheat sheet before relying on them.",
    },
    {
      id: "speed-row",
      targetSelector: "[data-hotspot-id='speed-row']",
      label: "Pan/tilt speed",
      body:
        "Sets how fast the joystick moves the camera (1 = slowest, 4 = fastest). Use slower speeds for on-air moves so motion looks deliberate; faster speeds are for repositioning between shots when off-air.",
    },
  ],
  shortcuts: [
    { keys: "↑ ↓ ← →", action: "Pan / tilt the active camera" },
    { keys: "0 – 9", action: "Recall preset (0=Establish, 1=Wide, 2=Lectern, 3=Spkr MCU, …)" },
    { keys: "+ / −", action: "Zoom in / out" },
    { keys: "[ / ]", action: "Focus near / far" },
  ],
};
